import { gql, useQuery, useApolloClient } from '@apollo/client';
import React, { useCallback } from 'react';

import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import { Route, Switch, useParams } from 'react-router-dom';
import { AUTOPAY_CONFIRMATION_PATH, PAYMENT_CONFIRMATION_PATH } from '../constants/routes';
import Autopay, { AUTOPAY_FRAGMENT } from './Autopay';
import BalanceAndStatus, { BALANCE_AND_STATUS_FRAGMENT } from './BalanceAndStatus';
import CollectionsNotes, { COLLECTIONS_NOTES_FRAGMENT } from './CollectionsNotes';
import ConfirmationModal from './ConfirmationModal';
import MostRecentInvoice, { MOST_RECENT_INVOICE_FRAGMENT } from './MostRecentInvoice';
import OwnerSelectCard, { OWNER_SELECT_CARD_FRAGMENT } from './OwnerSelectCard';
import Reversals, { REVERSALS_FRAGMENT } from './Reversals';
import SpecialInvoices, { SPECIAL_INVOICES_FRAGMENT } from './SpecialInvoices';
import TransactionHistory, { TRANSACTION_HISTORY_FRAGMENT } from './TransactionHistory';
import { useHybridAuthToken } from '../components/hybridAuthToken';
import rollbar from '../utils/rollbar';

const BILLING_PAGE_QUERY = gql`
  query BillingPageQuery($prospectId: String, $contactId: ID!, $hybridAuthToken: String) {
    prospect(prospectId: $prospectId) {
      id
      isVivintSolarPostPTO
      billing(contactId: $contactId, hybridAuthToken: $hybridAuthToken) {
        id
        lastModifiedDate
      }
      ...BalanceAndStatusFields
      ...MostRecentInvoiceFields
      ...AutopayFields
      ...OwnerSelectCardFields
      ...CollectionsNotesFields
      ...SpecialInvoiceFields
      ...TransactionHistoryFields
      ...ReversalsFields
    }
  }
  ${BALANCE_AND_STATUS_FRAGMENT}
  ${MOST_RECENT_INVOICE_FRAGMENT}
  ${AUTOPAY_FRAGMENT}
  ${OWNER_SELECT_CARD_FRAGMENT}
  ${COLLECTIONS_NOTES_FRAGMENT}
  ${SPECIAL_INVOICES_FRAGMENT}
  ${TRANSACTION_HISTORY_FRAGMENT}
  ${REVERSALS_FRAGMENT}
`;

const BillingPage = ({ currentLastDueDateUpdate, setCurrentLastDueDateUpdate }) => {
  const hybridAuthToken = useHybridAuthToken();
  const client = useApolloClient();

  const { prospectId, contactId } = useParams();
  const queryVariables = {
    prospectId,
    contactId,
    hybridAuthToken,
  };
  const { loading, data } = useQuery(BILLING_PAGE_QUERY, {
    variables: queryVariables,
    returnPartialData: true,
    skip: !hybridAuthToken,
  });

  const refreshBillingDataAfterAutopayDiscontinued = useCallback(async () => {
    if (!hybridAuthToken) return;

    const variables = {
      prospectId,
      contactId,
      hybridAuthToken,
    };

    const setAutopayEnrolledInCache = (enrolled) => {
      try {
        const existing = client.readQuery({ query: BILLING_PAGE_QUERY, variables });
        if (existing?.prospect) {
          client.writeQuery({
            query: BILLING_PAGE_QUERY,
            variables,
            data: {
              prospect: {
                ...existing.prospect,
                ACHEnabled: enrolled,
              },
            },
          });
        }
      } catch (_) {
        // Billing query is not in cache yet.
      }
    };

    setAutopayEnrolledInCache(false);

    await client.query({
      query: BILLING_PAGE_QUERY,
      variables,
      fetchPolicy: 'network-only',
    });

    try {
      const afterRefetch = client.readQuery({ query: BILLING_PAGE_QUERY, variables });
      if (afterRefetch?.prospect?.ACHEnabled) {
        setAutopayEnrolledInCache(false);
        rollbar.warn(`ACHEnabled still true after discontinue-autopay for prospectId: ${prospectId}`);
      }
    } catch (_) {
      // Billing query is not in cache after refetch.
    }
  }, [client, prospectId, contactId, hybridAuthToken]);

  const isVivintSolarPostPTO = data?.prospect?.isVivintSolarPostPTO;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid container direction="row" spacing={2} alignItems="stretch">
          <BalanceAndStatus
            data={data}
            loading={loading}
            isVivintSolarPostPTO={isVivintSolarPostPTO}
            currentLastDueDateUpdate={currentLastDueDateUpdate}
            setCurrentLastDueDateUpdate={setCurrentLastDueDateUpdate}
          />
          <MostRecentInvoice data={data} />
          <Autopay
            data={data}
            loading={loading}
            isVivintSolarPostPTO={isVivintSolarPostPTO}
            refetchBillingData={refreshBillingDataAfterAutopayDiscontinued}
          />
          <OwnerSelectCard data={data} loading={loading} />
        </Grid>
      </Grid>
      <CollectionsNotes data={data} />
      <SpecialInvoices data={data} loading={loading} />
      <TransactionHistory data={data} prospectId={prospectId} loading={loading} />
      <Reversals data={data} />
      <Switch>
        <Route path={PAYMENT_CONFIRMATION_PATH}>
          <ConfirmationModal />
        </Route>
        <Route path={AUTOPAY_CONFIRMATION_PATH}>
          <ConfirmationModal />
        </Route>
      </Switch>
    </Grid>
  );
};

BillingPage.propTypes = {
  currentLastDueDateUpdate: PropTypes.string,
  setCurrentLastDueDateUpdate: PropTypes.func,
};

export { BILLING_PAGE_QUERY };
export default BillingPage;
